// Documents — เอกสารของแต่ละแคมเปญ
//
// อัปโหลดผูกกับแคมเปญเสมอ (ไม่มีที่เก็บระดับแบรนด์) จึงมีกล่องอัปโหลดในทุกกลุ่ม
// เลือก "ทุกแคมเปญ" แล้วจะเห็นทุกกลุ่มเรียงกัน — เลือกแคมเปญเดียวก็เหลือกลุ่มนั้น
import { useState } from "react";
import type { Brand, Campaign } from "@contracts/api";
import { useSearchParams } from "react-router";
import { CampaignSelect } from "@features/campaigns/components/campaign-select";
import { useCampaigns } from "@features/campaigns/api/use-campaigns";
import { brandCfgLink } from "@features/navigation/model/side-menus";
import { apiErrorMessage } from "@shared/api/client";
import { type CampaignDocument } from "../api/documents-api";
import { useDocumentMutations, useDocuments } from "../api/use-documents";
import { DocumentCard } from "../components/document-card";
import { PreviewModal } from "../components/preview-modal";
import { MAX_UPLOAD_MB, UploadBox } from "../components/upload-box";
import { fileKind } from "../lib/file-kind";

export function DocumentsPage({ brand }: { brand: Brand }) {
  const [params] = useSearchParams();
  const pickedCampaign = params.get("campaign") ?? "";
  const cfgLink = brandCfgLink(brand);

  const campaigns = useCampaigns(brand.id);
  const docs = useDocuments(brand.id);
  const { upload, patch, remove } = useDocumentMutations(brand.id);
  const [previewing, setPreviewing] = useState<CampaignDocument | null>(null);

  const list = campaigns.data ?? [];
  const shown = pickedCampaign ? list.filter((c) => c.id === pickedCampaign) : list;
  const byCampaign = new Map<string, CampaignDocument[]>();
  for (const d of docs.data ?? []) {
    // เมนูนี้อัปโหลดผูกแคมเปญเสมอ ไฟล์ที่ไม่มีแคมเปญจึงเป็นของเมนูอื่น — ข้ามไป
    if (!d.campaignId) continue;
    const arr = byCampaign.get(d.campaignId);
    if (arr) arr.push(d);
    else byCampaign.set(d.campaignId, [d]);
  }

  /** อัปทีละไฟล์ตามลำดับ · คืนข้อความของไฟล์ที่ล้มเหลวให้กล่องอัปโหลดแสดง */
  async function uploadTo(campaignId: string, files: File[]): Promise<string[]> {
    const failed: string[] = [];
    for (const file of files) {
      try {
        // รูปเปิดพรีวิวในหน้าให้เลย เพราะเป็นสิ่งที่คนอยากเห็นทันที · ไฟล์อื่นสวิตช์นี้ไม่มีผล
        const preview = fileKind({ name: file.name, mime: file.type }) === "image";
        await upload.mutateAsync({ campaignId, file, preview });
      } catch (e) {
        failed.push(`${file.name} — ${apiErrorMessage(e)}`);
      }
    }
    return failed;
  }

  const busyId = (id: string) =>
    (patch.isPending && patch.variables?.id === id) ||
    (remove.isPending && remove.variables === id);

  return (
    <>
      <div className="content-head">
        <div>
          <div className="breadcrumb">{brand.name} / Documents</div>
          <h1>Documents</h1>
        </div>
        {!!list.length && <CampaignSelect campaigns={list} />}
      </div>

      {campaigns.isPending ? (
        <div className="panel">
          <p className="muted">กำลังโหลดแคมเปญ…</p>
        </div>
      ) : !list.length ? (
        <div className="panel empty-state">
          <h3>ยังไม่มีแคมเปญ</h3>
          <p className="muted">
            เอกสารต้องผูกกับแคมเปญ — สร้างแคมเปญก่อนที่{" "}
            <a href={cfgLink}>หน้าตั้งค่า → Campaigns</a>
          </p>
        </div>
      ) : docs.isError ? (
        <div className="panel empty-state">
          <h3>โหลดรายการเอกสารไม่สำเร็จ</h3>
          <p className="muted">{apiErrorMessage(docs.error)}</p>
        </div>
      ) : (
        shown.map((c) => (
          <CampaignDocs
            key={c.id}
            campaign={c}
            docs={byCampaign.get(c.id) ?? []}
            loading={docs.isPending}
            uploading={upload.isPending}
            busyId={busyId}
            onUpload={(files) => uploadTo(c.id, files)}
            onOpen={setPreviewing}
            onRename={(id, name) => patch.mutate({ id, name })}
            onTogglePreview={(id, preview) => patch.mutate({ id, preview })}
            onDelete={(id) => remove.mutate(id)}
          />
        ))
      )}

      {previewing && <PreviewModal doc={previewing} onClose={() => setPreviewing(null)} />}
    </>
  );
}

function CampaignDocs({
  campaign,
  docs,
  loading,
  uploading,
  busyId,
  onUpload,
  onOpen,
  onRename,
  onTogglePreview,
  onDelete,
}: {
  campaign: Campaign;
  docs: CampaignDocument[];
  loading: boolean;
  uploading: boolean;
  busyId: (id: string) => boolean;
  onUpload: (files: File[]) => Promise<string[]>;
  onOpen: (doc: CampaignDocument) => void;
  onRename: (id: string, name: string) => void;
  onTogglePreview: (id: string, preview: boolean) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="panel doc-panel">
      <div className="doc-panel-head">
        <h3>{campaign.name}</h3>
        <span className="muted">{docs.length ? `${docs.length} ไฟล์` : "ยังไม่มีเอกสาร"}</span>
      </div>

      <UploadBox
        disabled={uploading}
        hint={
          uploading
            ? "กำลังอัปโหลด…"
            : `รูปภาพ · PDF · Excel · PowerPoint · Word — ไฟล์ละไม่เกิน ${MAX_UPLOAD_MB}MB`
        }
        onFiles={onUpload}
      />

      {loading ? (
        <p className="muted">กำลังโหลดเอกสาร…</p>
      ) : (
        !!docs.length && (
          <div className="doc-grid">
            {docs.map((d) => (
              <DocumentCard
                key={d.id}
                doc={d}
                busy={busyId(d.id)}
                onOpen={() => onOpen(d)}
                onRename={(name) => onRename(d.id, name)}
                onTogglePreview={(preview) => onTogglePreview(d.id, preview)}
                onDelete={() => onDelete(d.id)}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
}
